import { PlusOutlined } from '@ant-design/icons'

import { GraviButton, GraviGrid, Horizontal, NothingMessage, Vertical } from '@gravitate-js/excalibrr'
import type { AgGridEvent, GridApi, IFilter } from 'ag-grid-community'
import { type MutableRefObject, useRef } from 'react'
import { UploadDownloadButtons } from '@components/_SiteWide/UploadStatus/UploadDownloadButtons'

import { CreateBaseFreightDrawer } from './components/CreateBaseFreightDrawer'
import { MileageBandDetails } from './components/MileageBandDetails'
import { useBaseFreightColumns, useBaseFreightState } from './hooks'
import type { BaseFreightGroup, Ratebook } from '@api/useFreight/types'

export function BaseFreightTab({
  ratebook,
}: {
  ratebook?: Ratebook
}): JSX.Element {
  const gridRef = useRef<GridApi<BaseFreightGroup>>(null)

  const {
    baseFreightGroups,
    selectedGroup,
    isEditMode,
    drawerVisible,
    editingBaseFreight,
    refetch,
    setSelectedGroup,
    setIsEditMode,
    openCreateDrawer,
    openEditDrawer,
    closeDrawer,
    handleCreateSuccess,
    exportBaseFreight,
    importBaseFreight,
  } = useBaseFreightState({
    ratebook,
    gridRef: gridRef as MutableRefObject<GridApi<BaseFreightGroup>>,
  })

  const columnDefs = useBaseFreightColumns({ openEditDrawer })

  const handleSelectionChanged = (event: AgGridEvent<BaseFreightGroup, IFilter>) => {
    const selectedRows = event.api.getSelectedRows()
    if (selectedRows.length > 0) {
      setSelectedGroup(selectedRows[0])
    }
  }
  return (
    <Horizontal style={{ margin: '0px -1em', height: '100%' }}>
      <Vertical flex={1}>
        <GraviGrid<BaseFreightGroup, IFilter>
          rowData={baseFreightGroups}
          externalRef={gridRef as MutableRefObject<GridApi<BaseFreightGroup>>}
          loading={!baseFreightGroups}
          agPropOverrides={{
            rowGroupPanelShow: 'never',
            columnDefs: columnDefs,
            getRowId: (params) => (params.data?.key ?? '') as string,
            rowSelection: 'single',
          }}
          onSelectionChanged={handleSelectionChanged}
          storageKey='ratebook-base-freight'
          controlBarProps={{
            title: 'Freight Tables',
            actionButtons: (
              <Horizontal gap={8}>
                <UploadDownloadButtons
                  fileName='FreightTables.xlsx'
                  refreshCallback={refetch}
                  downloadEP={() => exportBaseFreight(ratebook)}
                  uploadEP={(formData: FormData) => importBaseFreight(formData, ratebook)}
                  downloadEPData={null}
                  normalizeFunction={null}
                  asOfDate={null}
                  excelOptions={[
                    {
                      title: 'Download Freight Table Rates',
                      downloadEP: () => exportBaseFreight(ratebook),
                      fileName: 'FreightTables.xlsx',
                    },
                    {
                      title: 'Upload Freight Table Rates',
                      uploadEP: (formData: FormData) => importBaseFreight(formData, ratebook),
                    },
                  ]}
                />
                <GraviButton icon={<PlusOutlined />} onClick={openCreateDrawer} buttonText='Add Freight Table' theme2 />
              </Horizontal>
            ),
          }}
        />
      </Vertical>
      <Vertical style={{ minWidth: 500 }} flex={1}>
        {selectedGroup ? (
          <MileageBandDetails
            isEditMode={isEditMode}
            setIsEditMode={setIsEditMode}
            setSelectedGroup={setSelectedGroup}
            ratebook={ratebook as Ratebook}
            selectedGroup={selectedGroup}
          />
        ) : (
          <NothingMessage title='No Rate Selected' message='Select a rate from the grid to view its details' />
        )}
      </Vertical>

      <CreateBaseFreightDrawer
        ratebook={ratebook}
        visible={drawerVisible}
        onClose={closeDrawer}
        onSuccess={handleCreateSuccess}
        editingBaseFreight={editingBaseFreight}
      />
    </Horizontal>
  )
}
